import {Button, Input, Modal, Table, Switch, Tag, Select, Tooltip} from 'antd'
import {DeleteOutlined, EditOutlined, CloseOutlined} from '@ant-design/icons'
import Divider from 'antd/lib/divider'
import {useEffect, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {ItemResponsePublisherBeanObject, ListPagination, ListResponsePublisherBean, PublisherBean} from 'src/api/api'
import { useHistory } from 'react-router'
import {filterFunc, getFilterData, sorterFunc, useActionsMediaQuery} from 'src/util/Util'
import {StorageKey, storageReaderFactory, storageWriterFactory} from '../../../util/Storage'
import publisherApi from 'src/component/app/publisher/PublisherApi'
import Highlighter from 'react-highlight-words'
import './style.css'
import {useDebounce} from '../../../util/useDebounce'

const Column = Table.Column

const storageWriter = storageWriterFactory('publisher')
const storageReader = storageReaderFactory('publisher')

export const PublisherList = () => {
    const history = useHistory()
    const location = useLocation<{ fromPublisherList?: boolean }>()
    const [searchQuery, setSearchQuery] = useState('')
    const [searchType, setSearchType] = useState('name'); 
    const [data, setData] = useState<PublisherBean[]>([])
    const [pagination, setPagination] = useState<any>({
        current: 1,
        pageSize: 10,
        total: 0,
    })

    const [isFiltered, setIsFiltered] = useState(false)

    const areActionsVertical = useActionsMediaQuery('800px')

    const loadData = (page = 1, size?: number) => {
        const currentPageSize = size || pagination.pageSize;

        publisherApi
            .list(currentPageSize, page, {
                search: searchQuery,
                searchType: searchType
            })
            .then((listResponsePublisherBean: ListResponsePublisherBean) => {

                const array = listResponsePublisherBean.data as Array<ItemResponsePublisherBeanObject>
                const publisherBeans: PublisherBean[] = array.map(item => item.data as PublisherBean)

                if (publisherBeans.length === 0 && page > 1) {
                    loadData(page - 1, currentPageSize)
                } else {
                    const pageResponse: ListPagination =
                        listResponsePublisherBean.pagination as ListPagination

                    const storedPage = storageReader.read(StorageKey.SearchParams)?.page

                    // ✅ only update storage if page actually changed
                    if (!storedPage || storedPage !== pageResponse.currentPage) {
                        storageWriter.write(StorageKey.SearchParams, {
                            page: pageResponse.currentPage
                        })
                    }

                    setData(publisherBeans)
                    setPagination({
                        current: pageResponse.currentPage,
                        pageSize: currentPageSize,
                        total: pageResponse.total,
                    })
                }
            })
    }
    

    const initialLoad = () => {
        // ✅ Check if coming from Publisher Edit/Create pages
        const isFromPublisherPages = location.state?.fromPublisherList === true

        let page = 1 // Default to page 1

        if (isFromPublisherPages) {
            // ✅ If coming from Publisher Edit/Create, use stored page
            let searchParams = storageReader.read(StorageKey.SearchParams)
            page = searchParams?.page || pagination.current
        } else {
            // ✅ If coming from other pages, reset to page 1
            storageWriter.write(StorageKey.SearchParams, { page: 1 })
        }

        loadData(page)
    }

    useEffect(() => {
        initialLoad()
    }, [])

    const deletePublisher = (record: PublisherBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                publisherApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page, pagination.pageSize)
                    success();
                }).catch((err)=>{
                    Modal.error({
                        title: `Delete Error`,
                        content: err?.error,
                      });
                })
            }
        })
    }
    const success = () => {
        Modal.success({
          content: 'Deleted Successfully',
        });
      };
    let handleReset = () => {
        setSearchQuery('')
        setIsFiltered(false)
        loadData(1, pagination.pageSize)
    }

    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    useEffect(() => {
        // Set isFiltered to true when search query or type changes
        if (debouncedSearchQuery || searchType) {
            setIsFiltered(true);
        }

        // Only fetch if filtered or search is active
        if (isFiltered || debouncedSearchQuery) {
            loadData(1, pagination.pageSize);
        }
    }, [debouncedSearchQuery, searchType])

    const togglePublisherStatus = (record: PublisherBean) => {
        const isActive = record.status === 'ACTIVE';

        Modal.confirm({
            title: `${isActive ? 'Deactivate' : 'Activate'} ${record.name}?`,
            content: `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this publisher?`,
            onOk: () => {
                const action = isActive
                    ? publisherApi.deactivate(record.id as number)
                    : publisherApi.activate(record.id as number);
                action
                    .then((response) => {
                      // ✅ If activating and credentials are returned, show modal
                      if (!isActive && response?.data?.authDataBean) {
                        userCredentials(response.data.authDataBean);
                      } else {
                        loadData(pagination.current, pagination.pageSize);
                      }
                    })
                    .catch(err => {
                      Modal.error({
                        title: 'Status Update Failed',
                        content: err?.error || 'Unable to update publisher status',
                      });
                    });
            }
        })
    }
    const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: async () => {
                loadData(pagination.current, pagination.pageSize);
            }
        });
    };
    
    return (
        <div>
            <div style={{display: 'flex',marginBottom: '20px'}}>
            <div style={{ display: 'flex', gap: '8px', width: '500px' }}>
                <Select
                    value={searchType}
                    onChange={(value) => {
                        setSearchType(value)
                        setSearchQuery('')
                        setIsFiltered(false)
                    }}
                    style={{ width: '180px' }}
                >
                    <Select.Option value="name">Name</Select.Option>
                    <Select.Option value="id">Id</Select.Option>
                    <Select.Option value="sellerId">Seller Id</Select.Option>
                    <Select.Option value="status">Status</Select.Option>
                </Select>

                <Input
                    addonAfter={<CloseOutlined onClick={handleReset} />}
                    placeholder={
                        searchType === 'name'
                            ? 'Search by Publisher Name'
                            : searchType === 'id'
                            ? 'Search by Publisher Id'
                            : searchType === 'status'
                            ? 'Search by Publisher Status'
                            : 'Search by Seller Account Id'
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
            </div>

            </div>
            <Table
                rowKey="id"
                size="middle"
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page: number, pageSize?: number) => loadData(page, pageSize),
                    onShowSizeChange: (_: number, pageSize: number) => loadData(1, pageSize),
                }}
                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id"/>
                <Column key="sellerAccountId" title="Seller Account ID" dataIndex="sellerAccountId"/>
                <Column key="name" title="Name" dataIndex="name" filterMultiple
                        filters={getFilterData(data, bean => bean.name)}
                        onFilter={filterFunc(bean => bean.name)}
                        sorter={sorterFunc(bean => bean.name)}
                        render={text => searchQuery !== '' ?
                            <Highlighter
                                highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                                searchWords={[searchQuery]}
                                autoEscape
                                textToHighlight={text.toString()}/> :
                            text}
                />
                {/* <Column key="auctionMode" title="Auction Mode" dataIndex="auctionMode" render={text => text.replace(/([a-z])([A-Z])/g, '$1 $2')}/> */}
                <Column
                    key="status"
                    title="Status"
                    align="center"
                    dataIndex='status'
                    render={(status) =>
                        status === 'ACTIVE' ? (
                        <Tag color="green">Active</Tag>
                        ) : status === 'DRAFT' ? (
                        <Tag color="orange">Draft</Tag>
                        ) : (
                        <Tag color="red">Inactive</Tag>
                        )
                    }
                    />
                <Column align={'center'} key="action" title="Actions" render={(_: string, record: PublisherBean) => {
                    const status = (record as any).status;
                    return (
                    <span>
                        <Link
                            to={{
                                pathname: `/publishers/${record.id}/edit`,
                                state: { fromPublisherList: true },
                            }}
                            >
                            <Button htmlType="button" title="Edit">
                                <EditOutlined/>
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                                 type={areActionsVertical ? 'horizontal' : 'vertical'}/>
                        <Button
                            onClick={() => deletePublisher(record)}
                            type="primary"
                            htmlType="button"
                            danger
                            title="Delete">
                            <DeleteOutlined/>
                        </Button>
                        <>
                        <Divider className={`actions-horisontable-divider ${status === 'DRAFT' && 'invisible'}`}
                                 type={areActionsVertical ? 'horizontal' : 'vertical'}/>
                        <Tooltip title={status === 'ACTIVE' ? 'Active' : 'Inactive'} className={`${status === 'DRAFT' && 'invisible'}`}>
                            <Switch
                                checked={status === 'ACTIVE'}
                                onChange={() => togglePublisherStatus(record)}
                                className={`${status === 'DRAFT' && 'invisible'}`}
                            />
                        </Tooltip>
                        </>
                        
                    </span>
                    );
                }}/>
            </Table>
            <Link to={`/publishers/create`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    );
}